import { LeaderboardEntry } from "./Leaderboard";

/**
 * Request leaderboard data from the gaming backend.
 *
 * The gaming backend offers us two different endpoints to request leaderboard data:
 * - /api/high-scores/games/:gameId/leaderboard
 * - /api/high-scores/games/:gameId/runs/:runId/leaderboard
 *
 * The first endpoint returns the top 3 entries of the leaderboard, while the second
 * returns the global top score as well as 3 better and 3 worse scores.
 *
 * To get all top 10 entries, we need to make 4 requests:
 * 1. /api/high-scores/games/:gameId/leaderboard to get positions 1-3
 * 2. /api/high-scores/games/:gameId/runs/:runId/leaderboard, to get positions 4-6
 * 3. /api/high-scores/games/:gameId/runs/:runId/leaderboard, to get positions 7-9
 * 4. /api/high-scores/games/:gameId/runs/:runId/leaderboard, to get position 10
 *
 */

/**
 * Create a function to fetch the leaderboard data from the gaming backend.
 * The benefit of using a factory function is that we can inject a mock fetch function
 * for testing purposes and don't have to interact with live data during tests (as these might fail)
 */
export function createFetchLeaderboard({ fetchFn }: { fetchFn: typeof fetch }) {
  return async ({
    highscoreServiceEndpoint,
    gameId,
    fetchUntilPosition = 10,
  }: {
    highscoreServiceEndpoint: string;
    gameId: string;
    fetchUntilPosition?: number;
  }): Promise<LeaderboardEntry[]> => {
    // first check the top 3 for entries
    const top3 = await fetchFn(
      `${highscoreServiceEndpoint}/api/high-scores/games/${gameId}/leaderboard`,
    )
      .then((response) => response.json())
      .then((data) => data.data);

    // if there are less than 3 entries, there won't be any more to fetch from the leaderboard
    if (top3.length < 3) {
      return top3;
    }

    let leaderboard = [...top3];

    while (leaderboard.length < fetchUntilPosition) {
      // the leaderboard api provides 3 new entries per request,
      // so we can stop if the last request did not return 3 entries, as there are no more entries
      if (leaderboard.length % 3 !== 0) {
        break;
      }

      const lastEntry = leaderboard[leaderboard.length - 1];
      const runId = lastEntry.id;
      const nextEntries = await fetchFn(
        `${highscoreServiceEndpoint}/api/high-scores/games/${gameId}/runs/${runId}/leaderboard`,
      )
        .then((response) => response.json())
        .then((data) => data.data);

      // add entries that are not yet in result to result
      nextEntries.forEach((entry: LeaderboardEntry) => {
        if (!leaderboard.some((r) => r.id === entry.id)) {
          leaderboard = [...leaderboard, entry];
        }
      });
    }

    // return only the requested amount of entries
    return leaderboard.slice(0, fetchUntilPosition);
  };
}

export const fetchLeaderboard = createFetchLeaderboard({ fetchFn: fetch });
